import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    ExternalLink,
    Loader2,
    CheckCircle2,
    AlertCircle,
    Copy,
    Check,
    Sparkles,
    RefreshCw,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import type { CommentResult } from "../types";

interface ResultCardProps {
    result: CommentResult;
    onRetry?: () => void;
    onRegenerate?: () => void;
    onCopy: (text: string) => void;
}

export function ResultCard({ result, onRetry, onRegenerate, onCopy }: ResultCardProps) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        onCopy(result.comment);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const isLoading = result.status === "scraping" || result.status === "generating";

    return (
        <Card className="glass border border-white/5 overflow-hidden group hover:border-primary/20 transition-all duration-500">
            <div className="flex items-center justify-between p-3 px-5 border-b border-white/5 bg-white/2">
                <a
                    href={result.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-primary transition-colors truncate max-w-[60%]"
                >
                    <ExternalLink className="w-3 h-3" />
                    {result.url}
                </a>
                <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-black/20 border border-white/5">
                    <AnimatePresence mode="wait">
                        {result.status === "scraping" && (
                            <motion.div
                                key="scraping"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                className="flex items-center gap-1.5"
                            >
                                <Loader2 className="w-3 h-3 animate-spin text-primary" />
                                <span className="text-[10px] font-bold uppercase tracking-wider text-primary">Scraping</span>
                            </motion.div>
                        )}
                        {result.status === "generating" && (
                            <motion.div
                                key="generating"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                className="flex items-center gap-1.5"
                            >
                                <Sparkles className="w-3 h-3 animate-pulse text-purple-400" />
                                <span className="text-[10px] font-bold uppercase tracking-wider text-purple-400">AI Thinking</span>
                            </motion.div>
                        )}
                        {result.status === "done" && (
                            <motion.div
                                key="done"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="flex items-center gap-1.5"
                            >
                                <CheckCircle2 className="w-3 h-3 text-green-500" />
                                <span className="text-[10px] font-bold uppercase tracking-wider text-green-500">Ready</span>
                            </motion.div>
                        )}
                        {result.status === "error" && (
                            <motion.div
                                key="error"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="flex items-center gap-1.5"
                            >
                                <AlertCircle className="w-3 h-3 text-destructive" />
                                <span className="text-[10px] font-bold uppercase tracking-wider text-destructive">Failed</span>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            <CardContent className="p-5 px-6">
                <AnimatePresence mode="wait">
                    {result.status === "done" ? (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="space-y-4"
                        >
                            {(result.comments || [result.comment]).map((text, idx) => (
                                <div key={idx} className="space-y-3">
                                    <div className="relative p-4 rounded-2xl bg-white/5 border border-white/5 group-hover:bg-white/10 transition-colors">
                                        <p className="text-lg font-medium leading-relaxed tracking-tight">
                                            {text}
                                        </p>
                                        {result.comments && result.comments.length > 1 && (
                                            <div className="absolute top-2 right-2 text-[10px] uppercase font-bold text-white/20">
                                                Option {idx + 1}
                                            </div>
                                        )}
                                    </div>
                                    <Button
                                        onClick={() => {
                                            onCopy(text);
                                            setCopied(true);
                                            setTimeout(() => setCopied(false), 2000);
                                        }}
                                        className={`w-full h-11 rounded-xl transition-all duration-300 ${copied
                                            ? "bg-green-600 hover:bg-green-600"
                                            : "bg-primary/10 hover:bg-primary/20 text-white border border-primary/20"
                                            }`}
                                    >
                                        {copied ? (
                                            <>
                                                <Check className="w-4 h-4 mr-2" />
                                                Copied
                                            </>
                                        ) : (
                                            <>
                                                <Copy className="w-4 h-4 mr-2" />
                                                Copy Option {idx + 1}
                                            </>
                                        )}
                                    </Button>
                                </div>
                            ))}
                            {onRegenerate && (
                                <Button
                                    onClick={onRegenerate}
                                    variant="outline"
                                    className="w-full h-10 rounded-xl bg-white/5 border-white/10 hover:bg-white/10 text-white/60 hover:text-white"
                                >
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Regenerate Comment
                                </Button>
                            )}
                        </motion.div>
                    ) : result.status === "error" ? (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="p-4 bg-destructive/10 border border-destructive/20 rounded-2xl text-destructive text-sm space-y-3"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 font-bold">
                                    <AlertCircle className="w-4 h-4" />
                                    <span>Generation Error</span>
                                </div>
                                {onRetry && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 text-[10px] font-black uppercase tracking-widest bg-destructive/10 border-destructive/20 hover:bg-destructive/20 text-destructive"
                                        onClick={onRetry}
                                    >
                                        Retry Now
                                    </Button>
                                )}
                            </div>
                            <p className="opacity-80 pl-6 leading-relaxed">{result.error}</p>
                        </motion.div>
                    ) : (
                        <div className="py-8 flex flex-col items-center justify-center space-y-4">
                            <div className="flex gap-1.5">
                                {[0, 1, 2].map((i) => (
                                    <motion.div
                                        key={i}
                                        animate={{
                                            scale: [1, 1.5, 1],
                                            opacity: [0.3, 1, 0.3]
                                        }}
                                        transition={{
                                            duration: 1.5,
                                            repeat: Infinity,
                                            delay: i * 0.2
                                        }}
                                        className="w-2 h-2 rounded-full bg-primary"
                                    />
                                ))}
                            </div>
                            <p className="text-sm font-medium text-muted-foreground animate-pulse">
                                {result.status === "scraping" ? "Fetching post content..." : "Cooking up a sigma comment..."}
                            </p>
                        </div>
                    )}
                </AnimatePresence>
            </CardContent>
        </Card>
    );
}
